import { Copy, Delete, WidgetMoreOptions } from "assets/icons/Common";
import { Row, IconButton, Col, HorizontalLine } from "components/primitives";
import { DashboardContext } from "contexts/DashboardContext";
import { FC, useContext, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import styled from "styled-components";

interface Props {
  name: string;
  id: string;
  groupId: string;
  nameChanged: (id: string, name: string) => void;
  duplicateDashboard: (id: string) => void;
  deleteDashboard: (id: string) => void;
}

export const DashboardActions: FC<Props> = ({
  name,
  id,
  groupId,
  nameChanged,
  duplicateDashboard,
  deleteDashboard,
}) => {
  const { data: globalData } = useContext(DashboardContext);
  const nameInput = useRef<HTMLInputElement>(null);
  const namePara = useRef<HTMLInputElement>(null);
  const noBlurRef = useRef<HTMLInputElement>(null);
  const [nameValue, setNameValue] = useState(name);
  const [showOptionsPopup, setShowOptionsPopup] = useState(false);

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNameValue(e.target.value);
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (namePara.current) {
      namePara.current.style.display = "block";
    }
    if (e.target.value !== "") {
      nameChanged(id, e.target.value);
    }
    (e.target as HTMLElement).style.display = "none";
  };

  const handleNameClick: React.MouseEventHandler = (e) => {
    if (nameInput.current) {
      nameInput.current.style.display = "block";
      nameInput.current.focus();
      (e.target as HTMLElement).style.display = "none";
    }
  };

  const handleDuplicateClick: React.MouseEventHandler = () => {
    setShowOptionsPopup(false);
    duplicateDashboard(id);
  };

  const handleDeleteClick: React.MouseEventHandler = () => {
    setShowOptionsPopup(false);
    deleteDashboard(id);
  };

  const handleBlur: React.FocusEventHandler = (event) => {
    // Check if the next focused element (relatedTarget) is inside the noBlur element
    if (noBlurRef.current && !noBlurRef.current.contains(event.relatedTarget)) {
      setShowOptionsPopup(false);
    }
  };

  return (
    <ActionsWrap>
      <Name>
        <p ref={namePara} onClick={(e) => handleNameClick(e)}>
          {name || "New Dashboard"}
        </p>
        <NameInput
          ref={nameInput}
          type="text"
          value={nameValue}
          onBlur={handleNameChange}
          onChange={handleValueChange}
          maxLength={20}
        />
      </Name>
      <ActionButton
        Icon={WidgetMoreOptions}
        onClick={() => setShowOptionsPopup((prev) => !prev)}
        onBlur={handleBlur}
      />
      {showOptionsPopup && (
        <OptionsPopup ref={noBlurRef}>
          <TextBtn
            onClick={handleDuplicateClick}
            disabled={
              globalData.dashboardGroups.find((group) => group.id === groupId)!.dashboards.length >=
              10
            }
          >
            <Copy />
            Duplicate
          </TextBtn>
          <HorizontalLine style={{ margin: 0 }} />
          <TextBtn
            color="var(--danger)"
            onClick={handleDeleteClick}
            disabled={
              globalData.dashboardGroups.find((group) => group.id === groupId)?.dashboards
                ?.length === 1
            }
          >
            <Delete />
            Delete
          </TextBtn>
        </OptionsPopup>
      )}
    </ActionsWrap>
  );
};

const ActionsWrap = styled(Row)`
  margin-top: 10px;
  position: relative;
  align-items: center;
`;

const Name = styled.div`
  width: calc(100% - 24px);
  margin-left: 8px;

  p {
    width: 100%;
    line-height: 30px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const NameInput = styled(Form.Control)`
  width: 100%;
  display: none;
  padding: 0 4px;
  font-size: 14px;
  line-height: 1;
`;

const ActionButton = styled(IconButton)`
  width: 24px;
  height: 24px;
  box-shadow: none;
  background-color: transparent;
  flex-shrink: 0;
`;

const OptionsPopup = styled(Col)`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translate(-50%, 0);
  width: 190px;
  background-color: white;
  box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.05);
  border-radius: 8px;
  z-index: 1000;
`;

const TextBtn = styled.button<{ color?: string }>`
  border: none;
  box-shadow: none;
  background-color: transparent;
  display: flex;
  color: ${(props) => (props.color ? props.color : "#000")};
  align-items: center;
  padding: 10px 16px;
  gap: 5px;

  svg {
    width: 16px;
    height: 16px;
  }

  &:disabled {
    opacity: 0.5;
  }
`;
